import axios from "axios";
import React, { useEffect, useState } from "react";
import { Edit, Search, Slash } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Row,
  Col,
  Input,
  Button,
  Card,
  CardHeader,
  Table,
  Badge,
  Spinner,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  InputGroup,
  InputGroupText,
  Tooltip,
  UncontrolledTooltip,
} from "reactstrap";
import { getInventoryItems } from "../../../../redux/inventory";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import PurchaseEntry from "./Purchase/Bills/PurchaseEntry";
import PaginatedList from "./PaginatedList";

const StockManagementPage = (props) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const toggleTip = () => setTooltipOpen(!tooltipOpen);
  const itemsList = useSelector((state) => state.itemsList);
  const dispatch = useDispatch();
  const { itemsData, supplierData } = itemsList;
  const auth = useSelector((state) => state.authentication);
  const { userData } = auth;
  const [paginatedList, setPaginatedList] = useState([]);
  const [alertCount, setAlertCount] = useState(0);
  const [formData, setFormData] = useState(null);
  const [modal, setModal] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const navigate = useNavigate();
  const [customerDataDownload, setCustomerDataDownload] = useState([]);
  const [expiryAlertCount, setExpiryAlertCount] = useState(0);

  const toggle = () => setModal(!modal);
  const oneMonthLater = new Date();

  const renderLocationInfo = (location) => {
    if (!location) return { displayCode: "-", hoverDetails: "No location specified" };

    if (typeof location === "object") {
      const displayCode = location.code || "View Details";
      const details = [
        location.store && `Store: ${location.store}`,
        location.side && `Side: ${location.side}`,
        location.rack && `Rack: ${location.rack}`,
        location.row && `Row: ${location.row}`,
        location.cabin && `Cabin: ${location.cabin}`,
      ]
        .filter(Boolean)
        .join(" | ");

      return {
        displayCode,
        hoverDetails: details || displayCode,
      };
    }

    return {
      displayCode: String(location),
      hoverDetails: String(location),
    };
  };

  useEffect(() => {
    if (itemsList && itemsData?.length) {
      let count = 0;
      itemsData.forEach((item) => {
        if (Number(item.quantity) <= Number(item.reorderPoint) + 1) {
          count++;
        }
      });
      setAlertCount(count);

      let countX = 0;
      itemsData.forEach((item) => {
        const expiryDate = new Date(item.expiry);
        if (expiryDate < oneMonthLater && item.quantity > 0) {
          countX++;
        }
      });
      setExpiryAlertCount(countX);
    }

    if (itemsData && itemsData.length && supplierData && supplierData.length) {
      setCustomerDataDownload(
        itemsData.map((item, id) => ({
          SL_No: id + 1,
          Item: item.item,
          Brand: item.brandName,
          Price: item.unitPrice,
          MRP: item.mrp,
          Batch: item.batch,
          Expiry: item.expiry,
          HSN: item.hsn,
          Manufacturer: item.manufacturer,
          Quantity: item.quantity,
          Location:
            typeof item.location === "object"
              ? item.location?.code || JSON.stringify(item.location)
              : item.location,
          Category: item.category,
          Min_Stock: item.minStockLevel,
          Max_Stock: item.maxStockLevel,
          Reorder_Point: item.reorderPoint,
          Reorder_Qty: item.reorderQuantity,
          Supplier: supplierData.find(
            (sup) => sup.supplierId === item.supplierId
          )?.supplierName,
          Barcode: item.barcode,
          Notes: item.notes,
          ProductId: item.productId,
          Tax: item.tax,
        }))
      );
    }
  }, [itemsList]);

  const exportToExcel = (data) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "stock-list.xlsx");
  };

  const editInventoryHandler = (Data, toggleFn) => {
    setFormData(Data);
    toggleFn();
  };

  const deleteHandler = (item) => {
    const batch = prompt("Please enter the batch to confirm delete");
    if (
      batch &&
      batch.toLocaleLowerCase() === item.batch?.toLocaleLowerCase()
    ) {
      axios
        .post(
          `${process.env.REACT_APP_CRI_API}add-items`,
          {
            item: item,
            date_removed: new Date(),
            status: "",
            removedBy: userData.user,
          },
          {
            params: { branch: userData.branch },
            headers: { Authorization: `Bearer ${userData.token}` },
          }
        )
        .then(() => {
          axios
            .delete(`${process.env.REACT_APP_CRI_API}delete-inventory`, {
              params: { id: item._id, branch: userData.branch },
              headers: { Authorization: `Bearer ${userData.token}` },
            })
            .then((res) => {
              if (res) {
                dispatch(
                  getInventoryItems({
                    token: userData.token,
                    branch: userData.branch,
                  })
                );
              }
            })
            .catch((err) => {
              if (
                err.response?.data?.err?.name === "TokenExpiredError" ||
                err.response?.data?.message === "Unauthorized"
              ) {
                sessionStorage.clear();
                navigate("/cri-crm");
              }
            });
        });
    } else if (batch) {
      alert("Batch not matching");
    }
  };

  // Sticky header properties locked to the top of the browser viewport window
  const thStickyStyle = {
    position: "sticky",
    top: 0, // Sticks to the top of the viewport as the page scrolls up
    zIndex: 1020,
    backgroundColor: "#212529", // Solid dark background so text doesn't bleed through
    color: "#ffffff",
    boxShadow: "0 2px 2px -1px rgba(0, 0, 0, 0.4)",
  };

  return (
    <Container fluid className="p-0">
      {/* Top alert cards and search bar will scroll up naturally and hide */}
      <Row className="mx-0">
        <Col md={2}>
          <span id="expiry">
            <Card className="m-3">
              <CardHeader>
                <Row className="d-flex justify-content-center">
                  <h6>Expiry Alert </h6>
                  <Badge
                    color="danger"
                    style={{
                      width: "30px",
                      position: "absolute",
                      right: "-10px",
                      top: "-10px",
                    }}
                  >
                    {expiryAlertCount}
                  </Badge>
                </Row>
              </CardHeader>
            </Card>
          </span>
          <Tooltip
            placement="top"
            isOpen={tooltipOpen}
            autohide={false}
            target="expiry"
            toggle={toggleTip}
          >
            {itemsData?.length > 0 &&
              itemsData.map((item, id) => {
                const expiryDate = new Date(item.expiry);
                const isExpiringSoon = expiryDate < oneMonthLater;
                if (isExpiringSoon && item.quantity > 0)
                  return (
                    <p key={id}>
                      {item.item} {item.quantity} {item.batch}
                    </p>
                  );
                return null;
              })}
          </Tooltip>
        </Col>
        <Col md={3}>
          <Card className="m-3">
            <CardHeader>
              <Row className="d-flex justify-content-center">
                <h6>Stock Reorder Alert </h6>
                <Badge
                  color="warning"
                  style={{
                    width: "30px",
                    position: "absolute",
                    right: "-10px",
                    top: "-10px",
                  }}
                >
                  {alertCount}
                </Badge>
              </Row>
            </CardHeader>
          </Card>
        </Col>
        <Col md={3} className="pt-4">
          <InputGroup className="mb-4">
            <Input
              type="text"
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search Medicine Name"
            />
            <InputGroupText>
              <Search />
            </InputGroupText>
          </InputGroup>
        </Col>
        <Col className="pt-4">
          <Button
            color="primary"
            onClick={() => exportToExcel(customerDataDownload)}
          >
            Download
          </Button>
        </Col>
      </Row>

      <Row className="mx-0">
        <Col>
          <h5>Stock List</h5>
        </Col>
      </Row>

      <Row className="mx-0">
        <Col>
          <PaginatedList
            setPaginatedList={setPaginatedList}
            searchInput={searchInput}
            setShowSpinner={setShowSpinner}
            endPoint="inventory-page"
            limit={100}
          />
        </Col>
      </Row>

      {/* Main Table section with page-level scrolling */}
      <Row className="mx-0">
        <Col md={12}>
          {(!paginatedList || showSpinner) && <Spinner color="primary" />}
          {paginatedList && !showSpinner && paginatedList.length > 0 && (
            <div className="border rounded bg-white">
              <Table striped hover className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th style={thStickyStyle}>Sl No.</th>
                    <th style={{ ...thStickyStyle, width: "20%" }}>Item Name</th>
                    <th style={thStickyStyle}>HSN/SAC</th>
                    <th style={thStickyStyle}>Unit Price</th>
                    <th style={thStickyStyle}>MRP</th>
                    <th style={thStickyStyle}>Quantity</th>
                    <th style={thStickyStyle}>Batch</th>
                    <th style={thStickyStyle}>Location</th>
                    <th style={thStickyStyle}>Expiry Date</th>
                    <th style={thStickyStyle}>Edit</th>
                    <th style={thStickyStyle}>Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedList.map((item, id) => {
                    const expiryDate = new Date(item.expiry);
                    const isExpiringSoon = expiryDate < oneMonthLater;

                    const itemClass = isExpiringSoon
                      ? "table-danger"
                      : Number(item.quantity) <= Number(item.reorderPoint) + 1
                      ? "table-warning"
                      : "";

                    const loc = renderLocationInfo(item.location);
                    const tooltipId = `location-tooltip-${item._id || id}`;

                    if (item.item) {
                      return (
                        <tr key={item._id || id} className={itemClass}>
                          <td>{id + 1}</td>
                          <td>{item.item}</td>
                          <td>{item.hsn}</td>
                          <td>{item.unitPrice}</td>
                          <td>{item.mrp}</td>
                          <td>{item.quantity}</td>
                          <td>{item.batch}</td>
                          <td>
                            <span id={tooltipId} style={{ cursor: "pointer" }}>
                              <Badge color="info" className="text-wrap">
                                {loc.displayCode}
                              </Badge>
                            </span>
                            <UncontrolledTooltip
                              placement="top"
                              target={tooltipId}
                              delay={{ show: 0, hide: 0 }}
                            >
                              {loc.hoverDetails}
                            </UncontrolledTooltip>
                          </td>
                          <td>{item.expiry}</td>
                          <td>
                            {["Admin", "Manager", "Accounts"].includes(
                              userData.rights
                            ) && (
                              <Edit
                                color="green"
                                style={{ cursor: "pointer" }}
                                onClick={() => editInventoryHandler(item, toggle)}
                              />
                            )}
                          </td>
                          <td>
                            {["Admin", "Manager", "Accounts"].includes(
                              userData.rights
                            ) && (
                              <Slash
                                color="red"
                                style={{ cursor: "pointer" }}
                                onClick={() => deleteHandler(item)}
                              />
                            )}
                          </td>
                        </tr>
                      );
                    }
                    return null;
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Col>
      </Row>

      {/* Edit Inventory Modal */}
      <Modal isOpen={modal} toggle={toggle} fullscreen>
        <ModalHeader toggle={toggle}>Edit Inventory</ModalHeader>
        <ModalBody>
          {formData && (
            <PurchaseEntry
              key={formData._id || formData.productId || "edit-mode"}
              editInventoryData={formData}
              setShowModal={setShowModal}
              togglePurchaseEntry={toggle}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Saving Loader Overlay */}
      <Modal isOpen={showModal} centered>
        <ModalBody style={{ textAlign: "center", padding: "20px" }}>
          <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
          <p
            style={{ marginTop: "10px", fontSize: "16px", fontWeight: "bold" }}
          >
            Saving...
          </p>
        </ModalBody>
      </Modal>
    </Container>
  );
};

export default StockManagementPage;